"use client";

import { use, useEffect, useState } from "react";
import { useRouter } from "next/navigation";
import axios from "axios";
import { SiteNav } from "@/components/site-nav";
import { SiteFooter } from "@/components/site-footer";
import { LaptopInformationDetail } from "@/components/laptop-information-detail";
import type { LaptopInformation, LaptopScores, RecommendationsResponse } from "@/lib/types";
import { toSlug } from "@/lib/utils";

const API_BASE = process.env.NEXT_PUBLIC_API_BASE_URL ?? "https://laptop-search-tool-dev.onrender.com";

function LoadingSkeleton() {
  return (
    <div className="min-h-screen bg-background">
      <SiteNav />
      <main className="px-5 py-10 sm:px-8 sm:py-16">
        <div className="mx-auto max-w-7xl animate-pulse">
          {/* Back link */}
          <div className="h-4 w-28 rounded-full bg-foreground/8" />

          {/* Full-width title header */}
          <div className="mt-6 border-b border-border/60 pb-5">
            <div className="h-9 w-2/3 rounded-xl bg-foreground/8 sm:h-11" />
            <div className="mt-2.5 h-4 w-28 rounded-full bg-foreground/8" />
          </div>

          {/* Two-column grid */}
          <div className="mt-6 grid gap-10 lg:grid-cols-[1.1fr_1fr] lg:gap-16">
            {/* Image */}
            <div className="aspect-[4/3] w-full rounded-3xl bg-foreground/8" />

            {/* Right panel */}
            <div className="space-y-4">
              {/* Match score card */}
              <div className="rounded-2xl bg-foreground/8 p-5">
                <div className="flex items-center gap-3">
                  <div className="size-14 rounded-2xl bg-foreground/10" />
                  <div className="space-y-2">
                    <div className="h-4 w-16 rounded-full bg-foreground/10" />
                    <div className="h-3 w-28 rounded-full bg-foreground/10" />
                  </div>
                </div>
                <div className="mt-3 h-3 w-3/4 rounded-full bg-foreground/10" />
                <div className="mt-4 space-y-3 border-t border-border/40 pt-4">
                  {[1, 2, 3, 4].map((i) => (
                    <div key={i} className="space-y-1.5">
                      <div className="flex justify-between">
                        <div className="h-3 w-24 rounded-full bg-foreground/10" />
                        <div className="h-3 w-8 rounded-full bg-foreground/10" />
                      </div>
                      {i > 2 && <div className="h-1 w-full rounded-full bg-foreground/10" />}
                    </div>
                  ))}
                </div>
              </div>

              {/* Summary lines */}
              <div className="space-y-2">
                <div className="h-3.5 w-full rounded-full bg-foreground/8" />
                <div className="h-3.5 w-5/6 rounded-full bg-foreground/8" />
                <div className="h-3.5 w-4/6 rounded-full bg-foreground/8" />
              </div>

              {/* Price + button */}
              <div className="pt-2 space-y-3">
                <div className="h-9 w-32 rounded-xl bg-foreground/8" />
                <div className="h-12 w-full rounded-2xl bg-foreground/8" />
              </div>
            </div>
          </div>
        </div>
      </main>
    </div>
  );
}

export default function LaptopPage({
  params,
}: {
  params: Promise<{ country: string; series: string; config: string }>;
}) {
  const { country, series, config } = use(params);
  const router = useRouter();
  const [item, setItem] = useState<LaptopInformation | null | undefined>(undefined);
  const [matchScores, setMatchScores] = useState<LaptopScores | null>(null);
  const [scoresLoading, setScoresLoading] = useState(true);
  const [reference, setReference] = useState<Record<string, number | string | null>>({});
  const [answers, setAnswers] = useState<Record<string, string | string[] | number | null>>({});
  const [allItems, setAllItems] = useState<LaptopInformation[]>([]);

  useEffect(() => {
    try {
      const raw = sessionStorage.getItem("recommendations");
      const rawAnswers = sessionStorage.getItem("recommendation-answers");
      const rawRequest = sessionStorage.getItem("recommendation-request");

      if (rawAnswers) setAnswers(JSON.parse(rawAnswers));
      if (rawRequest) {
        const parsed = JSON.parse(rawRequest);
        if (parsed?.reference) setReference(parsed.reference);
      }

      if (!raw) { setItem(null); return; }
      const data: RecommendationsResponse = JSON.parse(raw);
      setAllItems(data.laptops);
      const found = data.laptops.find(
        (l) =>
          toSlug(l.laptop.series) === series &&
          String(l.laptop.config_number) === config,
      );
      setItem(found ?? null);
    } catch {
      setItem(null);
    }
  }, [series, config]);

  useEffect(() => {
    const fetchScores = async () => {
      try {
        const reqRaw = sessionStorage.getItem("recommendation-request");
        if (!reqRaw) { setScoresLoading(false); return; }
        const req = JSON.parse(reqRaw) as {
          country: string;
          max_price: number | undefined;
          brands: string[];
          allow_refurbished?: boolean;
          require_touchscreen?: boolean;
          reference: Record<string, unknown>;
          scalers: Record<string, number>;
        };

        const urlParams = new URLSearchParams();
        if (req.country) urlParams.set("country", req.country);
        if (req.max_price != null) urlParams.set("max_price", String(req.max_price));
        // Replaying the request without this would score the laptop against a
        // different candidate pool than the shortlist it was picked from.
        if (req.allow_refurbished) urlParams.set("allow_refurbished", "true");
        if (req.require_touchscreen) urlParams.set("require_touchscreen", "true");
        req.brands?.forEach((b) => urlParams.append("brands", b));

        const response = await axios.post<RecommendationsResponse>(
          `${API_BASE}/recommend/rec?${urlParams.toString()}`,
          { reference: req.reference, scalers: req.scalers },
        );

        const found = response.data.laptops.find(
          (l) =>
            toSlug(l.laptop.series) === series &&
            String(l.laptop.config_number) === config,
        );

        if (found) setMatchScores(found.scores);
      } catch {
        // scores section simply won't render
      } finally {
        setScoresLoading(false);
      }
    };

    fetchScores();
  }, [series, config]);

  // This page renders the personalised view backed by sessionStorage. When that
  // data is missing (e.g. a direct visit or shared link), redirect to the public
  // /spec twin, which renders the same laptop server-side, instead of 404ing.
  useEffect(() => {
    if (item === null) {
      router.replace(`/spec/${country}/${series}/${config}`);
    }
  }, [item, country, series, config, router]);

  const isLoading = item === undefined || scoresLoading;

  // Keep the skeleton up while loading, and while the redirect above is in flight.
  if (isLoading || item === null) return <LoadingSkeleton />;

  return (
    <div className="min-h-screen bg-background">
      <SiteNav />
      <LaptopInformationDetail
        item={item}
        matchScores={matchScores}
        reference={reference}
        answers={answers}
        allItems={allItems}
      />
      <SiteFooter />
    </div>
  );
}
